import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL, API_DOMAIN } from '../utils/config';
import { 
  FaPlus, FaSearch, FaCalendarAlt, FaFilePdf, FaBriefcase, 
  FaSync, FaInfoCircle, FaCheckCircle, FaUpload, FaTimes
} from 'react-icons/fa';

function MyJobs() {
  const [experiences, setExperiences] = useState([]);
  const [filteredExperiences, setFilteredExperiences] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '', role: '', location: '', salary: '', 
    startDate: '', endDate: '', offerLetter: null, payslip: null
  });

  const [currentJobData, setCurrentJobData] = useState(null);

  const getAuthInfo = () => {
    const userRole = localStorage.getItem("userRole");
    const isEmployee = userRole === "employee" || window.location.pathname.includes('/emp-');
    
    if (isEmployee) {
      const employeeDataRaw = localStorage.getItem("employeeData");
      let employeeId = null;
      if (employeeDataRaw) {
        try {
          const data = JSON.parse(employeeDataRaw);
          employeeId = data.employeeId || data._id;
        } catch (e) {}
      }
      if (!employeeId) employeeId = localStorage.getItem("employeeId");
      return employeeId ? { type: "employee", id: employeeId } : null;
    } else {
      const candidateToken = localStorage.getItem("candidateToken");
      const candidateId = localStorage.getItem("candidateId");
      if (candidateToken) {
        return { type: "candidate", token: candidateToken, id: candidateId };
      }
      // Fallback: check if they have an employeeId even if role isn't explicitly set
      const employeeId = localStorage.getItem("employeeId");
      if (employeeId) return { type: "employee", id: employeeId };
      
      return null;
    }
  };

  const fetchExperiences = async () => {
    const authInfo = getAuthInfo();
    if (!authInfo) {
      setErrorMsg("Authentication required.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      let response;
      if (authInfo.type === "candidate") {
        response = await axios.get(`${API_BASE_URL}/candidate/experience`, {
          headers: { Authorization: `Bearer ${authInfo.token}` }
        });
      } else {
        response = await axios.get(`${API_BASE_URL}/employees/experience/${authInfo.id}`);
      }

      if (response.data?.success) {
        const sorted = (response.data.data || []).sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
        setExperiences(sorted);
        setFilteredExperiences(sorted);
      }
    } catch (error) {
      setErrorMsg("Failed to synchronize records.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCurrentEmployeeJob = async () => {
    if (!window.location.pathname.includes('/emp-')) return;
    try {
      const email = localStorage.getItem("employeeEmail");
      if (!email) return;
      const res = await axios.get(`${API_BASE_URL}/employees/get-employees`);
      const match = (res.data || []).find(e => e.email?.toLowerCase() === email.toLowerCase());
      if (match) {
        setCurrentJobData({
          _id: "__current__",
          companyName: "Timely Health Tech Pvt Ltd",
          role: match.role || "-",
          location: match.city || "Remote",
          salary: match.salaryPerMonth ? `₹${match.salaryPerMonth.toLocaleString()}/mo` : "-",
          startDate: match.joinDate,
          endDate: null,
          __isCurrent: true,
        });
      }
    } catch (err) {}
  };

  useEffect(() => {
    fetchExperiences();
    fetchCurrentEmployeeJob();
  }, []);

  useEffect(() => {
    let filtered = experiences;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(exp => 
        exp.companyName?.toLowerCase().includes(term) || 
        exp.role?.toLowerCase().includes(term) || 
        exp.location?.toLowerCase().includes(term)
      );
    }
    if (selectedDate) {
      filtered = filtered.filter(exp => new Date(exp.startDate).toISOString().split("T")[0] === selectedDate);
    }
    if (selectedMonth) {
      const [y, m] = selectedMonth.split("-").map(Number);
      filtered = filtered.filter(exp => {
        const d = new Date(exp.startDate);
        return d.getFullYear() === y && d.getMonth() + 1 === m;
      });
    }
    setFilteredExperiences(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedDate, selectedMonth, experiences]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const authInfo = getAuthInfo();
    const data = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key]) data.append(key, formData[key]);
    });

    try {
      let res;
      if (authInfo.type === "candidate") {
        res = await axios.post(`${API_BASE_URL}/candidate/experience`, data, {
          headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${authInfo.token}` }
        });
      } else {
        data.append('employeeId', authInfo.id);
        res = await axios.post(`${API_BASE_URL}/employees/experience`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      if (res.data?.success) {
        fetchExperiences();
        setIsModalOpen(false);
        setFormData({ companyName: '', role: '', location: '', salary: '', startDate: '', endDate: '', offerLetter: null, payslip: null });
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Transmission failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'PRESENT';

  const renderDocumentUrl = (path) => {
    if (!path) return "#";
    // If it's already a full URL
    if (path.startsWith("http")) return path;

    // Normalize path separators
    const normalizedPath = path.replace(/\\/g, '/');
    
    // Check if it's an absolute path that contains 'uploads'
    if (normalizedPath.includes('/uploads/')) {
        const parts = normalizedPath.split('/uploads/');
        return `${API_DOMAIN}/uploads/${parts[1]}`;
    }
    
    // Otherwise assume it's relative to root or uploads
    const cleanPath = normalizedPath.startsWith('/') ? normalizedPath.substring(1) : normalizedPath;
    return `${API_DOMAIN}/${cleanPath}`;
  };

  const indexOfLast = currentPage * itemsPerPage;
  const currentRecords = filteredExperiences.slice(indexOfLast - itemsPerPage, indexOfLast);
  const totalPages = Math.ceil(filteredExperiences.length / itemsPerPage);

  if (isLoading) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      <p className="mt-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest animate-pulse">Loading Records...</p>
    </div>
  );

  return (
    <div className="w-full h-full">
      
      {/* ── Filters Bar ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-8">
        <div className="flex flex-wrap items-center gap-4">
          
          <div className="flex items-center gap-4 mr-auto">
             <div className="p-3 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl text-blue-600 shadow-sm border border-blue-100/50">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-blue-600 flex items-center justify-center text-white shadow-md">
                   <FaBriefcase size={20} />
                </div>
             </div>
             <div>
                <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-blue-600">EXPERIENCE</h1>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Professional history</div>
             </div>
          </div>

          <div className="relative flex-1 min-w-[150px]">
            <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="text"
              placeholder="Search by role or company..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs font-bold border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none uppercase tracking-widest"
            />
          </div>

          <input
            type="date"
            value={selectedDate}
            onChange={e => { setSelectedDate(e.target.value); setSelectedMonth("") }}
            className="w-32 py-1.5 px-3 text-[10px] font-bold border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none uppercase tracking-widest bg-white"
          />

          {(searchTerm || selectedDate || selectedMonth) && (
            <button
              onClick={() => { setSearchTerm(""); setSelectedDate(""); setSelectedMonth(""); }}
              className="h-7 px-3 text-[10px] font-bold text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition uppercase tracking-widest"
            >
              Clear
            </button>
          )}

          <button
            onClick={() => setIsModalOpen(true)}
            className="h-9 px-4 text-[10px] font-bold text-white bg-gradient-to-r from-green-500 to-blue-600 rounded-xl shadow-lg shadow-blue-100 flex items-center gap-1.5 hover:opacity-90 transition uppercase tracking-widest ml-2"
          >
            <FaPlus /> Add Experience
          </button>
        </div>
      </div>

      {/* ── Table Area ── */}
      <div className="p-0 bg-white border shadow-lg rounded-xl overflow-hidden">
        {filteredExperiences.length > 0 || currentJobData ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="text-xs text-left text-white bg-gradient-to-r from-green-500 to-blue-600">
                <tr>
                  <th className="py-3 px-4 text-center font-bold">Organization & Role</th>
                  <th className="py-3 px-4 text-center font-bold">Location</th>
                  <th className="py-3 px-4 text-center font-bold">Tenure Period</th>
                  <th className="py-3 px-4 text-center font-bold">Salary Details</th>
                  <th className="py-3 px-4 text-center font-bold">Documents</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentJobData && (
                  <tr className="bg-green-50/40">
                    <td className="px-4 py-2 flex items-center justify-center gap-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-ping"></div>
                      <div className="text-center">
                        <div className="text-xs font-bold text-gray-900">{currentJobData.role}</div>
                        <div className="text-[9px] font-bold text-green-600 uppercase tracking-widest mt-0.5">{currentJobData.companyName}</div>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-center text-[10px] font-bold text-gray-600 uppercase tracking-wider">{currentJobData.location}</td>
                    <td className="px-4 py-2 text-center text-[10px] font-medium text-gray-700">{formatDate(currentJobData.startDate)} — PRESENT</td>
                    <td className="px-4 py-2 text-center">
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[9px] font-bold rounded uppercase tracking-wider">ACTIVE</span>
                    </td>
                    <td className="px-4 py-2 text-center text-[9px] font-bold text-green-600 italic uppercase tracking-wider">Current Employment</td>
                  </tr>
                )}
                {currentRecords.map((exp, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2 text-center">
                      <div className="text-xs font-bold text-gray-800">{exp.role}</div>
                      <div className="text-[9px] font-bold text-gray-500 uppercase tracking-wider mt-0.5">{exp.companyName}</div>
                    </td>
                    <td className="px-4 py-2 text-center text-[10px] font-bold text-gray-600 uppercase tracking-wider">{exp.location}</td>
                    <td className="px-4 py-2 text-center text-[10px] font-medium text-gray-600">{formatDate(exp.startDate)} — {formatDate(exp.endDate)}</td>
                    <td className="px-4 py-2 text-center">
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[9px] font-bold rounded border border-blue-100 uppercase tracking-wider">{exp.salary}</span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex justify-center gap-3">
                        {exp.offerLetter && (
                          <a 
                            href={renderDocumentUrl(exp.offerLetter)} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="text-indigo-600 hover:text-indigo-800 transition-all active:scale-90" 
                            title="Offer Letter"
                          >
                            <FaFilePdf size={18}/>
                          </a>
                        )}
                        {exp.payslip && (
                          <a 
                            href={renderDocumentUrl(exp.payslip)} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="text-emerald-600 hover:text-emerald-800 transition-all active:scale-90" 
                            title="Payslip"
                          >
                            <FaFilePdf size={18}/>
                          </a>
                        )}
                        {!exp.offerLetter && !exp.payslip && <span className="text-[9px] font-bold text-gray-300 uppercase tracking-widest">No Docs</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-300">
              <FaBriefcase size={20} />
            </div>
            <h3 className="text-sm font-bold text-gray-700 mb-1">No Experience Found</h3>
            <p className="text-[10px] text-gray-400 font-medium tracking-widest">Add your professional experience by clicking "Add Experience".</p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="p-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
            <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">Page {currentPage} of {totalPages}</span>
            <div className="flex gap-2">
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(c => c-1)} className="px-3 py-1 bg-white border border-gray-200 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-gray-100 disabled:opacity-50 transition shadow-sm">Prev</button>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(c => c+1)} className="px-3 py-1 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-lg text-[10px] font-bold uppercase tracking-widest disabled:opacity-50 transition shadow-sm">Next</button>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest pt-6 pb-2">
        <FaInfoCircle className="text-blue-400" />
        Timely Health Professional Records • {new Date().getFullYear()}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white w-full max-w-lg rounded-2xl shadow-2xl p-8 relative overflow-hidden border border-gray-100">
               <div className="absolute top-0 right-0 p-6 cursor-pointer text-gray-400 hover:text-red-500 transition-colors" onClick={() => setIsModalOpen(false)}><FaTimes size={18}/></div>
               <div className="mb-6">
                  <h3 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-blue-600 uppercase tracking-widest mb-1">Add Experience</h3>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Update your professional history</p>
               </div>

               {errorMsg && <div className="mb-4 p-2 bg-red-50 text-red-600 rounded border border-red-100 text-[9px] font-bold uppercase tracking-wider text-center">{errorMsg}</div>}

               <form onSubmit={handleSubmit} className="space-y-4">
                 <div className="grid grid-cols-2 gap-4">
                     <div>
                       <label className="text-[9px] font-bold text-gray-500 uppercase tracking-widest ml-1 block mb-1">Company Name</label>
                       <input name="companyName" value={formData.companyName} onChange={e => setFormData({...formData, companyName: e.target.value})} required className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none text-xs font-bold text-gray-800" placeholder="e.g. Google" />
                     </div>
                     <div>
                        <label className="text-[9px] font-bold text-gray-500 uppercase tracking-widest ml-1 block mb-1">Job Role</label>
                        <input name="role" value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})} required className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none text-xs font-bold text-gray-800" placeholder="e.g. Software Engineer" />
                     </div>
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                     <div>
                        <label className="text-[9px] font-bold text-gray-500 uppercase tracking-widest ml-1 block mb-1">Location</label>
                        <input name="location" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} required className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none text-xs font-bold text-gray-800" placeholder="City, Country" />
                     </div>
                     <div>
                        <label className="text-[9px] font-bold text-gray-500 uppercase tracking-widest ml-1 block mb-1">Salary</label>
                        <input name="salary" value={formData.salary} onChange={e => setFormData({...formData, salary: e.target.value})} required className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none text-xs font-bold text-gray-800" placeholder="e.g. 50,000" />
                     </div>
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                     <div>
                        <label className="text-[9px] font-bold text-gray-500 uppercase tracking-widest ml-1 block mb-1">Start Date</label>
                        <input name="startDate" type="date" value={formData.startDate} onChange={e => setFormData({...formData, startDate: e.target.value})} required className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none text-[10px] font-bold" />
                     </div>
                     <div>
                        <label className="text-[9px] font-bold text-gray-500 uppercase tracking-widest ml-1 block mb-1">End Date (Current)</label>
                        <input name="endDate" type="date" value={formData.endDate} onChange={e => setFormData({...formData, endDate: e.target.value})} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none text-[10px] font-bold" />
                     </div>
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 border border-dashed border-blue-200 rounded-xl flex flex-col items-center gap-1 cursor-pointer hover:bg-blue-100 transition relative">
                       <FaUpload className="text-blue-500 text-[11px]" />
                       <span className="text-[9px] font-bold text-blue-700 uppercase tracking-widest text-center">{formData.offerLetter ? formData.offerLetter.name : "OFFER LETTER"}</span>
                       <input type="file" onChange={e => setFormData({...formData, offerLetter: e.target.files[0]})} className="absolute inset-0 opacity-0 cursor-pointer" />
                    </div>
                    <div className="p-4 bg-green-50 border border-dashed border-green-200 rounded-xl flex flex-col items-center gap-1 cursor-pointer hover:bg-green-100 transition relative">
                       <FaUpload className="text-green-500 text-[11px]" />
                       <span className="text-[9px] font-bold text-green-700 uppercase tracking-widest text-center">{formData.payslip ? formData.payslip.name : "PAYSLIP"}</span>
                       <input type="file" onChange={e => setFormData({...formData, payslip: e.target.files[0]})} className="absolute inset-0 opacity-0 cursor-pointer" />
                    </div>
                 </div>
                  <div className="flex gap-4 pt-4">
                     <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-3 text-[11px] font-bold text-gray-600 uppercase tracking-widest bg-gray-100 rounded-xl hover:bg-gray-200 transition">Cancel</button>
                     <button type="submit" disabled={isSubmitting} className="flex-1 py-3 text-[11px] font-bold text-white uppercase tracking-widest bg-gradient-to-r from-green-500 to-blue-600 rounded-xl shadow-lg shadow-blue-100 hover:opacity-90 transition disabled:opacity-50">{isSubmitting ? "Submitting..." : "Save Experience"}</button>
                  </div>
               </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MyJobs;